import * as React from 'react';
import { TApiContactsEntity } from '../../types/Contacts';
import Typography from '@mui/material/Typography';
import ListItem from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import PersonIcon from '@mui/icons-material/Person';
import ListItemText from '@mui/material/ListItemText';
import Avatar from '@mui/material/Avatar';
import ListItemSecondaryAction from '@mui/material/ListItemSecondaryAction';
import IconButton from '@mui/material/IconButton';
import PhoneIcon from '@mui/icons-material/Phone';
import { Theme } from '@mui/material/styles';

import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import { useAuthenticatedImage } from '../../hooks/useAuthenticatedImage';

interface IContactListProps {
  contact: TApiContactsEntity;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      width: '100%',
      borderBottom: `1px solid ${theme.palette.divider}`,
      '&:last-child': {
        borderBottom: 'none',
      },
    },
    avatar: {
      backgroundColor: theme.palette.grey[300],
      color: theme.palette.common.white,
      width: 80,
      height: 80,
      fontSize: '2rem',
      marginRight: "15px",
    },
    positionText: {
      display: 'block',
      color: theme.palette.text.secondary,
      fontSize: '0.875rem',
    },
    phoneText: {
      display: 'block',
      color: theme.palette.text.secondary,
      fontSize: '0.875rem',
    },
    callButton: {
      backgroundColor: theme.palette.success.main,
      color: theme.palette.common.white,
      '&:hover': {
        backgroundColor: theme.palette.success.dark,
      },
      width: 48,
      height: 48,
    },
    emailLink: {
      textDecoration: 'none',
      color: theme.palette.text.secondary,
      display: 'block',
      '&:hover': {
        color: theme.palette.primary.main,
      },
    },
  }),
);

const ContactListItem: React.FC<IContactListProps> =({
  contact,
}): JSX.Element => {
  const classes = useStyles({});
  const photoSrc = useAuthenticatedImage(contact.profilePhotoUrl);

  const cleanedPhoneNumber = contact.phoneNumber ? contact.phoneNumber.replace(/[\s-()]/g, '') : null;
  const hasPhoneNumber = !!cleanedPhoneNumber;

  const primaryText = contact.name;

  const secondaryText = (
    <React.Fragment>
      <Typography component="span" variant="body2" className={classes.positionText}>
        {contact.position}
      </Typography>

      {contact.email && (
        <a
          href={`mailto:${contact.email}`}
          className={classes.emailLink}
        >
          <Typography component="span" variant="body2" color="inherit">
            {contact.email}
          </Typography>
        </a>
      )}

      {hasPhoneNumber ? (
        <Typography component="span" variant="body2" className={classes.phoneText}>
          {contact.phoneNumber}
        </Typography>
      ) : ""}
    </React.Fragment>
  );

  return (
    <ListItem className={classes.root}>
      <ListItemAvatar>
        {photoSrc ? (
          <Avatar className={classes.avatar} src={photoSrc} alt={contact.name} />
        ) : (
          <Avatar className={classes.avatar}>
            <PersonIcon fontSize="large" />
          </Avatar>
        )}
      </ListItemAvatar>
      <ListItemText
        primary={primaryText}
        secondary={secondaryText}
        primaryTypographyProps={{ variant: 'h6' }}
      />
      {hasPhoneNumber && (
        <ListItemSecondaryAction>
          <IconButton
            edge="end"
            aria-label="call"
            className={classes.callButton}
            component="a"
            href={`tel:${cleanedPhoneNumber}`}
            size="large">
            <PhoneIcon />
          </IconButton>
        </ListItemSecondaryAction>
      )}
    </ListItem>
  );
};

export default ContactListItem;